import { el, clear, statusLabel, t } from './dom';
import { api, ApiError } from './api';
import type { Order, OrderStatus, User } from './types';
import { renderOrderEditor } from './orderEditorView';
import { renderProductCatalog } from './productCatalogView';
import { renderUserManagement } from './userManagementView';
import { buildLanguageSelector } from './languageSelector';
import { openChangePasswordModal } from './changePasswordModal';
import { COMPANY } from './config/company';

export async function renderAdminDashboard(root: HTMLElement, user: User, onLogout: () => void) {
  clear(root);

  const header = el('header', { className: 'header' });

  const brand = el('div', { className: 'header-brand' });
  const logoImg = document.createElement('img');
  logoImg.src = COMPANY.logoUrl;
  logoImg.alt = COMPANY.name;
  logoImg.className = 'header-logo';
  brand.appendChild(logoImg);
  brand.appendChild(el('h1', { className: 'header-title', text: `${COMPANY.name} · Admin` }));

  header.append(
    brand,
    el('div', { className: 'user-info' }, [
      buildLanguageSelector(() => renderAdminDashboard(root, user, onLogout)),
      el('button', {
        className: 'btn-logout',
        text: `📦 ${t('header.catalog')}`,
        onClick: () => renderProductCatalog(root, user, () => renderAdminDashboard(root, user, onLogout)),
      }),
      el('button', {
        className: 'btn-logout',
        text: `👥 ${t('users.title')}`,
        onClick: () => renderUserManagement(root, user, () => renderAdminDashboard(root, user, onLogout)),
      }),
      el('button', {
        className: 'btn-logout',
        text: `🔐 ${t('account.changePassword')}`,
        onClick: () => openChangePasswordModal(),
      }),
      el('span', { text: user.email }),
      el('button', { className: 'btn-logout', text: t('header.logout'), onClick: onLogout }),
    ]),
  );

  const main = el('main', { className: 'main' });

  // Tabs
  const tabs = el('div', { className: 'toolbar' });
  let currentStatus: OrderStatus | undefined = 'SUBMITTED';
  const statusOptions: Array<{ status: OrderStatus | undefined; labelKey: string }> = [
    { status: 'SUBMITTED', labelKey: 'admin.tabs.pending' },
    { status: 'UNDER_REVIEW', labelKey: 'admin.tabs.review' },
    { status: 'READY_TO_SEND', labelKey: 'admin.tabs.ready' },
    { status: 'SENT', labelKey: 'admin.tabs.sent' },
    { status: undefined, labelKey: 'admin.tabs.all' },
  ];

  const listContainer = el('div');

  const refresh = async () => {
    clear(listContainer);
    listContainer.appendChild(el('p', { text: t('common.loading'), className: 'loading' }));

    try {
      const qs = currentStatus ? `?status=${currentStatus}` : '';
      const data = await api.request<{ orders: Order[]; total: number }>('GET', `/admin/orders${qs}`);

      clear(listContainer);

      if (data.orders.length === 0) {
        listContainer.appendChild(el('div', {
          className: 'empty-state',
          text: t('admin.empty'),
        }));
        return;
      }

      const ul = el('ul', { className: 'order-list' });
      for (const order of data.orders) {
        const li = el('li', {
          className: 'order-item',
          onClick: () => {
            renderOrderEditor(root, order.id, user, () => renderAdminDashboard(root, user, onLogout));
          },
        });

        const titleRow = el('div', { className: 'card-header' });
        const meta = `${t('admin.byUser', { email: order.createdBy?.email ?? '—' })} · ${t('orders.weekOf', { week: order.weekNumber, year: order.year })} · ${t('orders.itemCount', { n: order.items.length })}`;
        titleRow.append(
          el('div', {}, [
            el('div', { className: 'card-title', text: order.orderNumber }),
            el('div', { className: 'order-meta', text: meta }),
          ]),
          el('span', { className: `badge badge-${order.status}`, text: statusLabel(order.status) }),
        );
        li.appendChild(titleRow);
        ul.appendChild(li);
      }
      listContainer.appendChild(ul);
    } catch (e) {
      clear(listContainer);
      const msg = e instanceof ApiError ? e.message : t('common.networkError');
      listContainer.appendChild(el('div', { className: 'empty-state', text: msg }));
    }
  };

  for (const opt of statusOptions) {
    const btn = el('button', {
      className: 'btn ' + (currentStatus === opt.status ? 'btn-primary' : 'btn-secondary'),
      text: t(opt.labelKey as any),
      onClick: async () => {
        currentStatus = opt.status;
        Array.from(tabs.children).forEach((c, i) => {
          const tabBtn = c as HTMLButtonElement;
          tabBtn.className = 'btn ' + (statusOptions[i].status === currentStatus ? 'btn-primary' : 'btn-secondary');
        });
        await refresh();
      },
    });
    tabs.appendChild(btn);
  }

  main.append(tabs, listContainer);
  root.append(header, main);

  await refresh();
}
